import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { createQuote, updateQuote, deleteQuote, listProducts, listCampuses } from "./api/quotes";

const STATUSES = ["DRAFT", "SENT", "ACCEPTED", "REJECTED"];

const currencySymbol = (c) => {
  const code = (c || "").toUpperCase();
  if (code === "EUR") return "€";
  if (code === "USD") return "$";
  return "£";
};
const brandNavy = [26, 31, 113];
const brandPink = [230, 0, 126];
const lightGrey = [245, 245, 245];

const blankLine = () => ({
  description: "",
  quantity: 1,
  unitPrice: 0,
  productId: null,
});

const blankQuote = () => ({
  title: "",
  clientName: "",
  clientEmail: "",
  clientAddress: "",
  campusId: "",
  quoteDate: dayjs().format("YYYY-MM-DD"),
  validUntil: dayjs().add(14, "day").format("YYYY-MM-DD"),
  currency: "GBP",
  status: "DRAFT",
  notes: "",
  depositType: "PERCENT",
  depositValue: 0,
  depositDueDate: "",
  lines: [blankLine()],
});

function drawBrandAccentText(pdf, text, xCenter, y, fontSize) {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(fontSize);
  const words = text.split(" ");
  const parts = [];
  words.forEach((word, i) => {
    if (!word) return;
    if (word.length >= 2) {
      parts.push({ text: word.slice(0, 2), color: brandPink });
      if (word.length > 2) parts.push({ text: word.slice(2), color: brandNavy });
    } else {
      parts.push({ text: word, color: brandPink });
    }
    if (i < words.length - 1) parts.push({ text: " ", color: brandNavy });
  });
  const total = parts.reduce((s, p) => s + pdf.getTextWidth(p.text), 0);
  let x = xCenter - total / 2;
  parts.forEach((p) => {
    pdf.setTextColor(...p.color);
    pdf.text(p.text, x, y);
    x += pdf.getTextWidth(p.text);
  });
}

function drawBrandAccentLabel(pdf, label, value, x, y) {
  pdf.setFont("helvetica", "bold");
  const first = label.slice(0, 2);
  const rest = label.slice(2);
  pdf.setTextColor(...brandPink);
  pdf.text(first, x, y);
  const o1 = pdf.getTextWidth(first);
  pdf.setTextColor(...brandNavy);
  pdf.text(rest + ": ", x + o1, y);
  const o2 = pdf.getTextWidth(rest + ": ");
  pdf.setTextColor(40);
  pdf.text(String(value || ""), x + o1 + o2, y);
}

function drawBrandAccentLabelRight(pdf, label, value, rightX, y) {
  pdf.setFont("helvetica", "bold");
  const first = label.slice(0, 2);
  const rest = label.slice(2) + ": ";
  const val = String(value || "");
  const w1 = pdf.getTextWidth(first);
  const w2 = pdf.getTextWidth(rest);
  const w3 = pdf.getTextWidth(val);
  const startX = rightX - (w1 + w2 + w3);
  pdf.setTextColor(...brandPink);
  pdf.text(first, startX, y);
  pdf.setTextColor(...brandNavy);
  pdf.text(rest, startX + w1, y);
  pdf.setTextColor(40);
  pdf.text(val, startX + w1 + w2, y);
}

export default function QuoteEditor({ open, onClose, quote, onSaved }) {
  const [draft, setDraft] = useState(blankQuote());
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedQuote, setSavedQuote] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(quote ? normaliseIncoming(quote) : blankQuote());
      setSavedQuote(quote || null);
      setDirty(!quote);
      setError("");
    }
  }, [open, quote]);

  useEffect(() => {
    if (!open) return;
    listProducts().then((d) => setProducts(Array.isArray(d) ? d : [])).catch(() => {});
    listCampuses().then((d) => setCampuses(Array.isArray(d) ? d : [])).catch(() => {});
  }, [open]);

  const total = useMemo(
    () =>
      (draft.lines || []).reduce(
        (acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0),
        0
      ),
    [draft.lines]
  );

  const depositAmount = useMemo(() => {
    const v = Number(draft.depositValue) || 0;
    if (draft.depositType === "PERCENT") return total * (v / 100);
    return v;
  }, [draft.depositType, draft.depositValue, total]);

  const patch = (p) => {
    setDirty(true);
    setDraft((d) => ({ ...d, ...p }));
  };
  const patchLine = (idx, p) => {
    setDirty(true);
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l, i) => (i === idx ? { ...l, ...p } : l)),
    }));
  };
  const addLine = () => {
    setDirty(true);
    setDraft((d) => ({ ...d, lines: [...d.lines, blankLine()] }));
  };
  const removeLine = (idx) => {
    setDirty(true);
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setError("");
    if (!draft.title?.trim() || !draft.clientName?.trim()) {
      setError("Title and Client Name are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        campusId: draft.campusId === "" ? null : draft.campusId,
        validUntil: draft.validUntil || null,
        depositType: draft.depositType || "PERCENT",
        depositValue: Number(draft.depositValue) || 0,
        depositDueDate: draft.depositDueDate || null,
        lines: draft.lines
          .filter((l) => l.description?.trim())
          .map((l, i) => ({
            position: i,
            description: l.description,
            quantity: Number(l.quantity) || 0,
            unitPrice: Number(l.unitPrice) || 0,
            productId: l.productId || null,
          })),
      };
      const currentId = savedQuote?.id || quote?.id;
      const saved = currentId
        ? await updateQuote(currentId, payload)
        : await createQuote(payload);
      if (saved) {
        setSavedQuote(saved);
        setDraft(normaliseIncoming(saved));
      }
      setDirty(false);
      onSaved(saved);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = savedQuote?.id || quote?.id;
    if (!id) return;
    if (!window.confirm("Delete this quote?")) return;
    try {
      await deleteQuote(id);
      onSaved(null);
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  const generatePDF = () => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 40;
    const logo = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
    const footerImg = process.env.PUBLIC_URL + "/assets/img/footer.png";

    const tableDefaults = {
      theme: "grid",
      margin: { left: 40, right: 40 },
      styles: { fontSize: 10, cellPadding: 6, textColor: 40, lineColor: [180, 180, 180], lineWidth: 0.5 },
      headStyles: { fillColor: brandNavy, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: lightGrey },
    };

    pdf.addImage(logo, "PNG", 40, y, 50, 60);
    drawBrandAccentText(pdf, "Quote", pageWidth - 90, y + 38, 20);

    const lineY = y + 70;
    pdf.setDrawColor(...brandNavy);
    pdf.setLineWidth(1);
    pdf.line(40, lineY, pageWidth - 40, lineY);
    y = lineY + 18;

    pdf.setFontSize(11);
    drawBrandAccentLabel(pdf, "Quote Ref", (savedQuote?.id || quote?.id) ? `Q-${savedQuote?.id || quote?.id}` : "(unsaved)", 40, y);
    const dateStr = draft.quoteDate ? dayjs(draft.quoteDate).format("DD-MM-YYYY") : "";
    drawBrandAccentLabelRight(pdf, "Date", dateStr, pageWidth - 40, y);
    y += 20;
    drawBrandAccentLabel(pdf, "Client", draft.clientName || "", 40, y);
    if (draft.validUntil) {
      drawBrandAccentLabelRight(pdf, "Valid Until", dayjs(draft.validUntil).format("DD-MM-YYYY"), pageWidth - 40, y);
    }
    y += 20;
    if (draft.clientEmail) {
      drawBrandAccentLabel(pdf, "Email", draft.clientEmail, 40, y);
      y += 20;
    }
    if (draft.clientAddress) {
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(40);
      const addrLines = pdf.splitTextToSize(draft.clientAddress, pageWidth - 80);
      pdf.text(addrLines, 40, y);
      y += addrLines.length * 14;
    }

    y += 6;
    drawBrandAccentText(pdf, draft.title || "Quoted Items", pageWidth / 2, y, 13);
    y += 8;

    autoTable(pdf, {
      ...tableDefaults,
      startY: y,
      head: [["Description", "Qty", "Unit Price", "Amount"]],
      body: (draft.lines || [])
        .filter((l) => l.description?.trim())
        .map((l) => {
          const qty = Number(l.quantity) || 0;
          const up = Number(l.unitPrice) || 0;
          return [
            l.description,
            qty.toString(),
            `${currencySymbol(draft.currency)} ${up.toFixed(2)}`,
            `${currencySymbol(draft.currency)} ${(qty * up).toFixed(2)} ${draft.currency || "GBP"}`,
          ];
        }),
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 50, halign: "right" },
        2: { cellWidth: 80, halign: "right" },
        3: { cellWidth: 100, halign: "right" },
      },
    });

    y = pdf.lastAutoTable.finalY + 14;
    autoTable(pdf, {
      ...tableDefaults,
      startY: y,
      head: [["Summary", "Amount"]],
      body: (() => {
        const rows = [["Total", `${currencySymbol(draft.currency)} ${total.toFixed(2)} ${draft.currency || "GBP"}`]];
        const depVal = Number(draft.depositValue) || 0;
        if (depVal > 0) {
          const label =
            draft.depositType === "PERCENT"
              ? `Deposit (${depVal}%)${draft.depositDueDate ? ` due ${dayjs(draft.depositDueDate).format("DD-MM-YYYY")}` : ""}`
              : `Deposit${draft.depositDueDate ? ` due ${dayjs(draft.depositDueDate).format("DD-MM-YYYY")}` : ""}`;
          rows.push([label, `${currencySymbol(draft.currency)} ${depositAmount.toFixed(2)} ${draft.currency || "GBP"}`]);
          rows.push(["Balance", `${currencySymbol(draft.currency)} ${(total - depositAmount).toFixed(2)} ${draft.currency || "GBP"}`]);
        }
        return rows;
      })(),
      columnStyles: {
        0: { cellWidth: 340, fontStyle: "bold" },
        1: { cellWidth: "auto", halign: "right" },
      },
    });

    const depVal = Number(draft.depositValue) || 0;
    if (depVal > 0) {
      y = pdf.lastAutoTable.finalY + 14;
      const amountStr = `${currencySymbol(draft.currency)} ${depositAmount.toFixed(2)} ${draft.currency || "GBP"}`;
      const dueStr = draft.depositDueDate
        ? ` by ${dayjs(draft.depositDueDate).format("DD-MM-YYYY")}`
        : "";
      const typeStr =
        draft.depositType === "PERCENT"
          ? `a ${depVal}% deposit of ${amountStr}`
          : `a deposit of ${amountStr}`;
      const termsLine = `Terms: ${typeStr}${dueStr} is required to confirm this quote.`;
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(10);
      pdf.setTextColor(40);
      const termsLines = pdf.splitTextToSize(termsLine, pageWidth - 80);
      pdf.text(termsLines, 40, y);
      y += termsLines.length * 14;
    }

    pdf.setPage(pdf.getNumberOfPages());
    pdf.setDrawColor(...brandNavy);
    pdf.setLineWidth(1);
    pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);
    pdf.addImage(footerImg, "PNG", (pageWidth - 120) / 2, pageHeight - 55, 120, 35);

    const fname = `Quote_${(draft.clientName || "client").replace(/\s+/g, "_")}_${dateStr || "today"}.pdf`;
    pdf.save(fname);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {(savedQuote?.id || quote?.id) ? `Edit Quote Q-${savedQuote?.id || quote?.id}` : "New Quote"}
        {(savedQuote?.createdByName || quote?.createdByName) && (
          <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
            created by {savedQuote?.createdByName || quote?.createdByName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Title"
              value={draft.title}
              onChange={(e) => patch({ title: e.target.value })}
              fullWidth
              required
            />
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={draft.status}
                onChange={(e) => patch({ status: e.target.value })}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Client Name"
              value={draft.clientName}
              onChange={(e) => patch({ clientName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Client Email"
              value={draft.clientEmail}
              onChange={(e) => patch({ clientEmail: e.target.value })}
              fullWidth
            />
          </Stack>

          <TextField
            label="Client Address"
            value={draft.clientAddress}
            onChange={(e) => patch({ clientAddress: e.target.value })}
            fullWidth
            multiline
            minRows={2}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="stretch">
            <DatePicker
              label="Quote Date"
              value={draft.quoteDate ? dayjs(draft.quoteDate) : null}
              onChange={(v) =>
                patch({
                  quoteDate: v ? v.format("YYYY-MM-DD") : "",
                  validUntil: v ? v.add(14, "day").format("YYYY-MM-DD") : draft.validUntil,
                })
              }
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Valid Until"
              value={draft.validUntil ? dayjs(draft.validUntil) : null}
              onChange={(v) => patch({ validUntil: v ? v.format("YYYY-MM-DD") : "" })}
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true } }}
              size={"small"}
            />
            <TextField
              select
              label="Campus"
              value={draft.campusId ?? ""}
              size={"small"}
              onChange={(e) => patch({ campusId: e.target.value })}
              sx={{ minWidth: 220 }}
              SelectProps={{
                sx: {
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                },
              }}
            >
              <MenuItem value="">(none)</MenuItem>
              {campuses.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Currency"
              value={draft.currency}
              onChange={(e) => patch({ currency: e.target.value.toUpperCase() })}
              sx={{ width: 300 }}
              inputProps={{ maxLength: 3 }}
            />
          </Stack>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="h6">Line Items</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add Line</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "45%" }}>Description</TableCell>
                  <TableCell sx={{ width: 100 }}>Qty</TableCell>
                  <TableCell sx={{ width: 120 }}>Unit Price</TableCell>
                  <TableCell sx={{ width: 120 }} align="right">Amount</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.lines.map((line, idx) => {
                  const amount = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <Autocomplete
                          freeSolo
                          options={products}
                          getOptionLabel={(o) => (typeof o === "string" ? o : o.name || "")}
                          isOptionEqualToValue={(o, v) =>
                            (typeof o === "string" ? o : o?.id) ===
                            (typeof v === "string" ? v : v?.id)
                          }
                          renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                              <li {...rest} key={option.id ?? option.name}>
                                {option.name}
                              </li>
                            );
                          }}
                          value={line.description}
                          onInputChange={(_, v) => patchLine(idx, { description: v })}
                          onChange={(_, v) => {
                            if (v && typeof v !== "string") {
                              patchLine(idx, {
                                description: v.name || "",
                                unitPrice: v.defaultRate ?? line.unitPrice,
                                productId: v.id || null,
                              });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} variant="standard" placeholder="Description" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          type="number"
                          value={line.quantity}
                          onChange={(e) => patchLine(idx, { quantity: e.target.value })}
                          inputProps={{ step: "0.01" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          type="number"
                          value={line.unitPrice}
                          onChange={(e) => patchLine(idx, { unitPrice: e.target.value })}
                          inputProps={{ step: "0.01" }}
                        />
                      </TableCell>
                      <TableCell align="right">{currencySymbol(draft.currency)} {amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeLine(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={3} align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{currencySymbol(draft.currency)} {total.toFixed(2)} {draft.currency || "GBP"}</strong></TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Deposit</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  size="small"
                  value={draft.depositType || "PERCENT"}
                  onChange={(e) => patch({ depositType: e.target.value })}
                >
                  <MenuItem value="PERCENT">Percentage</MenuItem>
                  <MenuItem value="AMOUNT">Fixed amount</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={draft.depositType === "PERCENT" ? "Percent (%)" : "Amount"}
                type="number"
                value={draft.depositValue}
                onChange={(e) => patch({ depositValue: e.target.value })}
                inputProps={{ step: "0.01", min: 0 }}
                sx={{ width: 160 }}
              />
              <DatePicker
                label="Due Date"
                value={draft.depositDueDate ? dayjs(draft.depositDueDate) : null}
                onChange={(v) => patch({ depositDueDate: v ? v.format("YYYY-MM-DD") : "" })}
                format="DD/MM/YYYY"
                slotProps={{ textField: { sx: { width: 200 } } }}
              />
              <Typography sx={{ ml: { sm: "auto" } }}>
                Deposit due: <strong>{currencySymbol(draft.currency)} {depositAmount.toFixed(2)} {draft.currency || "GBP"}</strong>
              </Typography>
            </Stack>
          </Box>

          <TextField
            label="Notes"
            value={draft.notes}
            onChange={(e) => patch({ notes: e.target.value })}
            fullWidth
            multiline
            minRows={2}
          />

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {(savedQuote?.id || quote?.id) && (
          <Button color="error" onClick={handleDelete} sx={{ mr: "auto" }}>
            Delete
          </Button>
        )}
        <Button startIcon={<PictureAsPdfIcon />} onClick={generatePDF}>PDF</Button>
        {dirty && <Button onClick={onClose}>Cancel</Button>}
        {dirty ? (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function normaliseIncoming(q) {
  return {
    title: q.title || "",
    clientName: q.clientName || "",
    clientEmail: q.clientEmail || "",
    clientAddress: q.clientAddress || "",
    campusId: q.campusId ?? "",
    quoteDate: q.quoteDate || dayjs().format("YYYY-MM-DD"),
    validUntil: q.validUntil || "",
    currency: q.currency || "GBP",
    status: q.status || "DRAFT",
    notes: q.notes || "",
    depositType: q.depositType || "PERCENT",
    depositValue: q.depositValue ?? 0,
    depositDueDate: q.depositDueDate || "",
    lines: (q.lines && q.lines.length ? q.lines : [blankLine()]).map((l) => ({
      description: l.description || "",
      quantity: l.quantity ?? 1,
      unitPrice: l.unitPrice ?? 0,
      productId: l.productId || null,
    })),
  };
}
